import { Download, ExternalLink, Loader2 } from 'lucide-react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/primitives'

export type DocumentPreview = {
  title: string
  subtitle?: string
  url: string
  contentType: string
}

export type DocumentPreviewDialogProps = {
  open: boolean
  isLoading: boolean
  error: string | null
  preview: DocumentPreview | null
  onClose: () => void
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/')
}

function isRenderableImage(contentType: string): boolean {
  return (
    contentType === 'image/jpeg' ||
    contentType === 'image/png' ||
    contentType === 'image/webp' ||
    contentType === 'image/gif'
  )
}

export function DocumentPreviewDialog({
  open,
  isLoading,
  error,
  preview,
  onClose,
}: DocumentPreviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="flex h-[85vh] max-w-4xl flex-col gap-3">
        <DialogHeader>
          <DialogTitle>{preview?.title ?? 'Document'}</DialogTitle>
          {preview?.subtitle ? (
            <p className="text-sm text-muted-foreground">{preview.subtitle}</p>
          ) : null}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-card">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
              {error}
            </div>
          ) : !preview ? null : isRenderableImage(preview.contentType) ? (
            <div className="flex h-full items-center justify-center overflow-auto p-3">
              <img
                src={preview.url}
                alt={preview.title}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : isImage(preview.contentType) ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <p className="text-sm text-muted-foreground">
                This image format can't be previewed in the browser.
              </p>
              <Button asChild variant="outline">
                <a href={preview.url} target="_blank" rel="noopener noreferrer">
                  <Download className="size-4" />
                  Download to view
                </a>
              </Button>
            </div>
          ) : (
            <object
              data={preview.url}
              type={preview.contentType}
              title={preview.title}
              className="size-full"
            >
              <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  This document can't be previewed in your browser.
                </p>
                <Button asChild variant="outline">
                  <a href={preview.url} target="_blank" rel="noopener noreferrer">
                    <Download className="size-4" />
                    Download to view
                  </a>
                </Button>
              </div>
            </object>
          )}
        </div>

        {preview ? (
          <div className="flex items-center justify-end gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={preview.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-4" />
                Open in new tab
              </a>
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
